import { useCallback, useEffect, useState } from 'react';
import type { Api, SmsView } from '../api';
import type { T } from '../i18n';
import { DangerButton, Field } from '../ui';
import { errorText } from './AgentSettings';

type Msg = { kind: 'ok' | 'error'; text: string };

/** Parts of an SMS by the provider's table (same rule as the server; shown while typing). */
const smsPartsOf = (text: string): number => {
  const ascii = /^[\x20-\x7E\n\r€£¥èéùìòÇØøÅåÄÖÑÜäöñüàß§¿¡É]*$/u;
  const turkish = /^[\x20-\x7E\n\r€£¥èéùìòÇØøÅåÄÖÑÜäöñüàß§¿¡ÉŞşĞğİıç]*$/u;
  const [one, many] = ascii.test(text) ? [160, 153] : turkish.test(text) ? [155, 149] : [70, 67];
  const n = [...text].length;
  return n === 0 ? 0 : n <= one ? 1 : Math.ceil(n / many);
};

/** ADR-067: the workspace's own SMS account (provider credentials, approved sender), a test message, the log. */
export const SmsAccountCard = ({ api, t }: { api: Api; t: T }) => {
  const [view, setView] = useState<SmsView>();
  const [editing, setEditing] = useState(false);
  const [x, setX] = useState({ username: '', password: '', sender: '' });
  const [test, setTest] = useState({ to: '', text: '' });
  const [senders, setSenders] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>();

  const load = useCallback(() => {
    api.sms().then(setView).catch((e: unknown) => setMsg({ kind: 'error', text: errorText(t, e) }));
  }, [api, t]);
  useEffect(load, [load]);

  const run = async (f: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(undefined);
    try {
      const r = await f();
      const s = (r as { senders?: string[] } | undefined)?.senders;
      if (s) setSenders(s);
      setMsg({ kind: 'ok', text: ok });
      load();
      return true;
    } catch (e) {
      setMsg({ kind: 'error', text: errorText(t, e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const account = view?.account;
  const configured = account?.configured === true;
  const startEdit = () => {
    setX({ username: configured ? account.username : '', password: '', sender: configured ? (account.sender ?? '') : '' });
    setEditing(true);
  };
  const save = () =>
    void run(
      () => api.saveSms({ provider: 'verimor', username: x.username.trim(), sender: x.sender.trim() || null, ...(x.password ? { password: x.password } : {}) }),
      t('sms.saved'),
    ).then((ok) => ok && setEditing(false));
  const parts = smsPartsOf(test.text.trim());

  return (
    <div className="section-gap">
      <h2 className="section-title">{t('sms.title')}</h2>
      <p className="hint">{t('sms.hint')}</p>
      {msg && <div className={`banner ${msg.kind} small`}>{msg.text}</div>}
      {view && !configured && !editing && (
        <div className="card line compact-line">
          <div className="row-between">
            <span className="muted">{t('sms.none')}</span>
            <button type="button" className="primary" onClick={startEdit}>{t('sms.connect')}</button>
          </div>
        </div>
      )}
      {editing && (
        <div className="card line-form">
          <h3>{t('sms.provider.verimor')}</h3>
          <Field label={t('sms.username')} hint={t('sms.username.hint')}>
            <input value={x.username} inputMode="numeric" autoComplete="off" onChange={(e) => setX({ ...x, username: e.target.value })} />
          </Field>
          <Field label={t('sms.password')} hint={configured ? t('lines.password.keep') : t('sms.password.hint')}>
            <input type="password" value={x.password} autoComplete="new-password" onChange={(e) => setX({ ...x, password: e.target.value })} />
          </Field>
          <Field label={t('sms.sender')} hint={t('sms.sender.hint')}>
            <input value={x.sender} list="sms-senders" maxLength={20} onChange={(e) => setX({ ...x, sender: e.target.value })} />
            <datalist id="sms-senders">{senders.map((s) => <option key={s} value={s} />)}</datalist>
          </Field>
          <p className="hint">{t('lines.secretNote')}</p>
          <div className="row-between section-gap">
            <span />
            <span className="row">
              <button type="button" className="ghost" onClick={() => setEditing(false)}>{t('settings.discard')}</button>
              <button type="button" className="primary" disabled={busy || !x.username.trim() || (!configured && !x.password)} onClick={save}>
                {busy ? t('sms.checking') : t('sms.save')}
              </button>
            </span>
          </div>
        </div>
      )}
      {configured && !editing && (
        <>
          <div className="card line compact-line">
            <div className="row-between">
              <div>
                <div className="row">
                  <strong>{t(`sms.provider.${account.provider}`)}</strong>
                  <span className={`pill ${account.verified ? 'ok' : 'warn'}`}>{account.verified ? t('sms.verified') : t('sms.unverified')}</span>
                </div>
                <div className="hint mono">{account.username}{account.sender ? ` · ${account.sender}` : ''}</div>
              </div>
              <span className="row">
                <button type="button" className="ghost" onClick={startEdit}>{t('lines.edit')}</button>
                <DangerButton t={t} label={t('settings.remove')} onConfirm={() => void run(() => api.removeSms(), t('sms.removed'))} />
              </span>
            </div>
          </div>
          <div className="card line-form">
            <h3>{t('sms.test')}</h3>
            <Field label={t('sms.test.to')} hint={t('sms.test.to.hint')}>
              <input value={test.to} inputMode="tel" placeholder="+90…" onChange={(e) => setTest({ ...test, to: e.target.value })} />
            </Field>
            <Field label={t('sms.test.text')} hint={t('sms.parts', { n: parts })}>
              <textarea rows={3} maxLength={480} value={test.text} onChange={(e) => setTest({ ...test, text: e.target.value })} />
            </Field>
            <div className="row-between">
              <span className="hint">{t('sms.limits')}</span>
              <button type="button" className="primary" disabled={busy || !test.to.trim() || !test.text.trim()} onClick={() => void run(() => api.testSms(test.to.trim(), test.text.trim()), t('sms.test.sent'))}>
                {t('sms.test.send')}
              </button>
            </div>
          </div>
          {view && view.messages.length > 0 && (
            <table className="table">
              <thead><tr><th>{t('sms.log.when')}</th><th>{t('sms.log.to')}</th><th>{t('sms.log.kind')}</th><th>{t('sms.log.status')}</th><th>{t('sms.log.text')}</th></tr></thead>
              <tbody>
                {view.messages.map((m) => (
                  <tr key={m.id}>
                    <td className="nowrap">{new Date(m.createdAt).toLocaleString()}</td>
                    <td className="mono">{m.to}</td>
                    <td>{t(`sms.kind.${m.kind}`) === `sms.kind.${m.kind}` ? m.kind : t(`sms.kind.${m.kind}`)}</td>
                    <td>
                      <span className={`pill ${m.status === 'sent' ? 'ok' : 'error'}`}>{t(`sms.status.${m.status}`)}</span>
                      {m.error && <div className="hint">{t(`error.${m.error}`) === `error.${m.error}` ? m.error : t(`error.${m.error}`)}</div>}
                    </td>
                    <td className="hint">{m.body.length > 80 ? `${m.body.slice(0, 80)}…` : m.body}{m.parts > 1 ? ` (${m.parts})` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};
